import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import {
  useGetSubscriptionQuery,
  useGetUsageStatsQuery,
  useGetPlanLimitsQuery,
  useCreatePortalSessionMutation,
  useCreateAidlcCheckoutMutation,
  useContactSalesMutation,
} from '@/store/api/apiSlice';
import {
  FiExternalLink,
  FiStar,
  FiSend,
  FiMessageSquare,
  FiCheck,
  FiAlertCircle,
  FiUsers,
  FiInfo,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import AidlcBillingMatrix from '@/components/billing/AidlcBillingMatrix';
import PlanFeaturesCard from '@/components/billing/PlanFeaturesCard';
import BillingInvoicesPanel from '@/components/billing/BillingInvoicesPanel';
import BillingTaxSetupPanel from '@/components/billing/BillingTaxSetupPanel';
import OrgAiUsagePanel from '@/products/org/components/OrgAiUsagePanel';
import { AIDLC_NAME } from '@/config/products';
import { PRODUCT_BILLING } from '@/config/billingCatalog';
import { PLAN_PRICE_USD_PER_SEAT } from '@/config/planFeatures';
import { useBillingCTA } from '@/hooks/useBillingCTA';
import { getApiError } from '@/utils/errorUtils';
import { startBillingCheckout } from '@/utils/razorpayCheckout';

type BillingTab = 'overview' | 'products' | 'invoices';

const Billing: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const tabs = useMemo(
    () =>
      ([
        { id: 'overview', label: t('billing.tabs.overview') },
        { id: 'products', label: t('billing.tabs.products') },
        { id: 'invoices', label: t('billing.tabs.invoices') },
      ] as const),
    [t],
  );
  const [activeTab, setActiveTab] = useState<BillingTab>('overview');
  const { data: subscription, isLoading: subscriptionLoading } = useGetSubscriptionQuery(undefined);
  const { data: usage, isLoading: usageLoading } = useGetUsageStatsQuery(undefined);
  const { data: limits } = useGetPlanLimitsQuery(undefined);
  const [createPortal, { isLoading: portalLoading }] = useCreatePortalSessionMutation();
  const [createCheckout, { isLoading: checkoutLoading }] = useCreateAidlcCheckoutMutation();
  const [contactSales, { isLoading: contactLoading }] = useContactSalesMutation();
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactMessage, setContactMessage] = useState('');
  const [contactSent, setContactSent] = useState(false);
  const [contactError, setContactError] = useState('');

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'overview' || tab === 'products' || tab === 'invoices') {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const billingState = subscription?.billingState;
  const billingConfigured = subscription?.billingConfigured ?? false;
  const billingProvider = subscription?.billingProvider ?? 'none';
  const billingAccess = subscription?.billingAccess as
    | {
        isComped?: boolean;
        paymentRequired?: boolean;
        suspendReason?: string | null;
      }
    | null
    | undefined;
  const cta = useBillingCTA(billingState, !!subscription?.billing, {
    billingConfigured,
    billingAccess,
  });
  const usageTierLabel = subscription?.usageTierLabel ?? 'Starter';

  const usersUsed = usage?.usage?.users?.current ?? usage?.users ?? 0;
  const billableSeats = Math.max(1, subscription?.billableSeats ?? usersUsed ?? 1);
  const seatsByProduct = subscription?.billableSeatsByProduct ?? {
    pm: billableSeats,
    code: 0,
    ship: 0,
    lenc: 0,
  };
  const estimatedMonthly =
    subscription?.estimatedMonthlyUsd ??
    subscription?.estimatedPlanMonthlyUsd ??
    billableSeats * PLAN_PRICE_USD_PER_SEAT;

  const handleManageSubscription = async () => {
    if (!billingConfigured) {
      toast.info('Online billing is not configured for this environment.');
      return;
    }
    try {
      const result = await createPortal(undefined).unwrap();
      if (result?.inAppOnly) {
        toast.info(result.message || 'Contact billing@cotaskr.com to manage your subscription.');
        return;
      }
      const portalUrl = (result?.portalUrl ?? (result as { url?: string })?.url ?? '').trim();
      if (!portalUrl) {
        toast.error('Billing portal did not return a link. Try again or contact support.');
        return;
      }
      const opened = window.open(portalUrl, '_blank', 'noopener,noreferrer');
      if (!opened) window.location.assign(portalUrl);
    } catch (error: unknown) {
      toast.error(getApiError(error, 'Could not open the billing portal.'));
    }
  };

  const handleEnablePlan = async () => {
    if (!billingConfigured) {
      toast.info('Online billing is not configured for this environment.');
      return;
    }
    try {
      const result = await createCheckout({
        interval: 'monthly',
        products: [{ productId: 'pm', enabled: true }],
      }).unwrap();
      await startBillingCheckout(result);
    } catch (error: unknown) {
      const message = getApiError(error, 'Checkout failed.');
      if (message !== 'Checkout cancelled') toast.error(message);
    }
  };

  const handlePrimaryCta = () => {
    if (cta.primaryAction === 'subscribe') void handleEnablePlan();
    else if (cta.primaryAction === 'manage') void handleManageSubscription();
    else if (cta.primaryAction === 'contact_sales') handleContactSales();
  };

  const handleContactSales = () => {
    setContactMessage('');
    setContactSent(false);
    setContactError('');
    setShowContactModal(true);
  };

  const handleSendContactMessage = async () => {
    if (!contactMessage.trim()) return;
    try {
      const result = await contactSales({ message: contactMessage.trim() }).unwrap();
      setContactSent(true);
      setContactMessage('');
      if (result?.message) {
        toast.success(result.message);
      }
    } catch (error: unknown) {
      setContactError(getApiError(error, 'Failed to send. Try again or email sales@cotaskr.com directly.'));
    }
  };

  const formatUsage = (used: number, max: number | null) => {
    if (max === null || max === -1) return `${used} / ∞`;
    return `${used} / ${max}`;
  };

  const formatStorage = (usedBytes: number, limitBytes: number | null) => {
    if (limitBytes == null || limitBytes === -1) return `${Math.round(usedBytes / (1024 * 1024))} MB / ∞`;
    const usedGb = usedBytes / (1024 * 1024 * 1024);
    const limGb = limitBytes / (1024 * 1024 * 1024);
    if (limGb >= 1) return `${usedGb.toFixed(1)} / ${limGb.toFixed(0)} GB`;
    return `${Math.round(usedBytes / (1024 * 1024))} / ${Math.round(limitBytes / (1024 * 1024))} MB`;
  };

  const usagePct = (used: number, max: number | null) => {
    if (!max || max === -1) return 0;
    return Math.min((used / max) * 100, 100);
  };

  const barColor = (pct: number) => {
    if (pct >= 90) return 'bg-red-500';
    if (pct >= 75) return 'bg-amber-500';
    return 'bg-indigo-500';
  };

  const projectsUsed = usage?.usage?.projects?.current ?? usage?.projects ?? 0;
  const aiUsed = usage?.usage?.aiActions?.current ?? usage?.aiActions ?? 0;
  const storageUsed = usage?.usage?.storage?.currentBytes ?? 0;
  const storageLimit = usage?.usage?.storage?.limitBytes ?? null;

  const maxUsers = limits?.limits?.maxUsers ?? limits?.maxUsers ?? null;
  const maxProjects = limits?.limits?.maxProjects ?? limits?.maxProjects ?? null;
  const maxAI = limits?.limits?.aiActionsPerMonth ?? limits?.maxAIActionsPerMonth ?? null;

  const storagePct = usagePct(storageUsed, storageLimit);
  const pageLoading = subscriptionLoading || usageLoading;

  const subscriptionStatus = subscription?.billing?.status?.replace(/_/g, ' ');

  const bannerIcon = useMemo(() => {
    if (cta.bannerSeverity === 'warning') return FiAlertCircle;
    if (cta.bannerSeverity === 'error') return FiAlertCircle;
    return FiInfo;
  }, [cta.bannerSeverity]);
  const BannerIcon = bannerIcon;

  return (
    <div className="relative h-full flex flex-col" style={{ backgroundColor: 'var(--bg)' }}>
      <div
        className="flex-1 flex flex-col overflow-hidden min-h-0"
        style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.10)', backgroundColor: 'var(--surface)' }}
      >
        <div className="px-4 py-2.5 shrink-0" style={{ borderBottom: '1px solid var(--border-strong)' }}>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 w-full">
            <span
              className="inline-flex items-center justify-center h-7 w-7 rounded-lg shrink-0"
              style={{ backgroundColor: 'var(--surface-active)', border: '1px solid var(--border-focus)' }}
            >
              <FiStar className="text-brand w-3.5 h-3.5" />
            </span>

            <div className="min-w-0 flex-1 basis-[180px]">
              <h1 className="text-base font-bold tracking-tight leading-none text-text-primary">
                {t('billing.title', { product: AIDLC_NAME })}
              </h1>
              <p className="text-[11px] text-text-muted/80 mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-indigo-100 text-indigo-800">
                  {usageTierLabel}
                </span>
                {subscriptionStatus && (
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium capitalize ${
                      subscription?.billing?.status === 'active' ||
                      subscription?.billing?.status === 'trialing'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    {subscriptionStatus}
                  </span>
                )}
                <span className="text-[10px] text-slate-500 tabular-nums">
                  Est. ${estimatedMonthly}/mo
                </span>
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2 shrink-0 text-[10px]">
              <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface-hover/50 px-2 py-1">
                <span className="text-text-muted">Plan</span>
                <strong className="text-text-primary tabular-nums">{seatsByProduct.pm ?? 0}</strong>
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-md border border-violet-200 bg-violet-50/60 px-2 py-1">
                <span className="text-violet-700">Code</span>
                <strong className="text-text-primary tabular-nums">
                  {seatsByProduct.code ?? 0}×${PRODUCT_BILLING.code.priceUsd}
                </strong>
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50/60 px-2 py-1">
                <span className="text-amber-700">Ship</span>
                <strong className="text-text-primary tabular-nums">
                  {seatsByProduct.ship ?? 0}×${PRODUCT_BILLING.ship.priceUsd}
                </strong>
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-2 ms-auto shrink-0">
              {cta.primaryLabel && (
                <button
                  type="button"
                  onClick={handlePrimaryCta}
                  disabled={portalLoading || checkoutLoading || (cta.primaryAction !== 'contact_sales' && !billingConfigured && (cta.primaryAction === 'subscribe' || cta.primaryAction === 'manage'))}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
                >
                  {checkoutLoading || portalLoading ? t('billing.pleaseWait') : cta.primaryLabel}
                </button>
              )}
              {cta.showManageBilling && cta.primaryAction !== 'manage' && billingConfigured && (
                <button
                  type="button"
                  onClick={() => void handleManageSubscription()}
                  disabled={portalLoading}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-surface text-xs font-semibold text-text-secondary hover:bg-surface-hover"
                >
                  <FiExternalLink className="w-3.5 h-3.5" />
                  Billing help
                </button>
              )}
              {cta.secondaryLabel && (
                <button
                  type="button"
                  onClick={handleContactSales}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                >
                  <FiMessageSquare className="w-3.5 h-3.5" />
                  {cta.secondaryLabel}
                </button>
              )}
            </div>
          </div>

          <div className="mt-2 flex gap-1 border-b border-border/60 -mb-px">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`px-3 py-1.5 text-[11px] font-semibold rounded-t-md border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-brand text-brand bg-brand/5'
                    : 'border-transparent text-text-muted hover:text-text-secondary hover:bg-surface-hover/50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 px-4 py-3 space-y-4" style={{ backgroundColor: 'var(--bg)' }}>
          {pageLoading ? (
            <div className="space-y-4 animate-pulse">
              <div className="h-16 rounded-xl bg-slate-100" />
              <div className="h-40 rounded-2xl bg-slate-100" />
              <div className="h-32 rounded-2xl bg-slate-100" />
            </div>
          ) : (
            <>
              {cta.bannerMessage && (
                <div
                  className={`rounded-xl px-4 py-3 flex gap-2 ${
                    cta.bannerSeverity === 'warning'
                      ? 'border border-amber-200 bg-amber-50'
                      : cta.bannerSeverity === 'error'
                        ? 'border border-red-200 bg-red-50'
                        : 'border border-indigo-100 bg-indigo-50/50'
                  }`}
                >
                  <BannerIcon
                    className={`shrink-0 mt-0.5 w-4 h-4 ${
                      cta.bannerSeverity === 'warning'
                        ? 'text-amber-600'
                        : cta.bannerSeverity === 'error'
                          ? 'text-red-600'
                          : 'text-indigo-600'
                    }`}
                  />
                  <p className="text-[12px] leading-relaxed text-slate-800">{cta.bannerMessage}</p>
                </div>
              )}

              {!billingConfigured && (
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 flex gap-2 text-[12px] text-slate-600">
                  <FiInfo className="shrink-0 mt-0.5 w-4 h-4 text-slate-500" />
                  <p>
                    Online billing is not active in this environment. Usage limits and trials still apply.
                    Checkout and invoices are disabled until billing is configured.
                  </p>
                </div>
              )}

              {activeTab === 'overview' && (
                <>
                  {(subscription?.billing || subscription?.hasPaidSubscription) && billingConfigured && (
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                        <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                          <p className="text-slate-500">Subscription</p>
                          <p className="font-semibold text-slate-800 mt-0.5 capitalize">
                            {subscriptionStatus ?? subscription?.tenantStatus ?? '—'}
                          </p>
                        </div>
                        <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                          <p className="text-slate-500">Period ends</p>
                          <p className="font-semibold text-slate-800 mt-0.5">
                            {subscription?.billing?.currentPeriodEnd
                              ? new Date(subscription.billing.currentPeriodEnd * 1000).toLocaleDateString()
                              : '—'}
                          </p>
                        </div>
                        <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                          <p className="text-slate-500">Cancellation</p>
                          <p className="font-semibold text-slate-800 mt-0.5">
                            {subscription?.billing?.cancelAtPeriodEnd
                              ? 'Ends at period close'
                              : subscription?.hasPaidSubscription
                                ? 'Active'
                                : '—'}
                          </p>
                        </div>
                        <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 flex items-center">
                          <button
                            type="button"
                            onClick={() => void handleManageSubscription()}
                            disabled={portalLoading}
                            className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 font-semibold disabled:opacity-60"
                          >
                            <FiExternalLink className="w-3.5 h-3.5" />
                            {portalLoading ? 'Opening…' : billingProvider === 'razorpay' ? 'Billing help' : 'Payment & invoices'}
                          </button>
                        </div>
                      </div>
                      {subscription?.billing?.cancelAtPeriodEnd && (
                        <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900 mt-3">
                          <FiAlertCircle className="shrink-0 mt-0.5" />
                          <span>
                            Subscription cancels at period end.{' '}
                            {billingProvider === 'razorpay'
                              ? 'Contact billing@cotaskr.com to resume or update payment.'
                              : 'Open the billing portal to resume or update payment.'}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  <PlanFeaturesCard
                    seatCount={billableSeats}
                    planDaysLeft={billingState?.planDaysLeft}
                    planState={billingState?.planState}
                    hasPaidSubscription={subscription?.hasPaidSubscription}
                  />

                  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
                    <h2 className="text-sm font-semibold text-slate-700 mb-4">Usage this month</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                      {[
                        { label: 'Users', used: usersUsed, max: maxUsers },
                        { label: 'Projects', used: projectsUsed, max: maxProjects },
                        { label: 'AI actions', used: aiUsed, max: maxAI },
                      ].map(({ label, used, max }) => {
                        const pct = usagePct(used, max);
                        return (
                          <div key={label}>
                            <div className="flex items-center justify-between text-xs mb-1.5">
                              <span className="text-slate-500 font-medium">{label}</span>
                              <span
                                className={`font-semibold ${pct >= 90 ? 'text-red-600' : pct >= 75 ? 'text-amber-600' : 'text-slate-700'}`}
                              >
                                {formatUsage(used, max)}
                              </span>
                            </div>
                            <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all ${barColor(pct)}`}
                                style={{ width: `${Math.max(pct, 2)}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                      <div>
                        <div className="flex items-center justify-between text-xs mb-1.5">
                          <span className="text-slate-500 font-medium">Storage</span>
                          <span
                            className={`font-semibold ${storagePct >= 90 ? 'text-red-600' : storagePct >= 75 ? 'text-amber-600' : 'text-slate-700'}`}
                          >
                            {formatStorage(storageUsed, storageLimit)}
                          </span>
                        </div>
                        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${barColor(storagePct)}`}
                            style={{ width: `${Math.max(storagePct, 2)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                    {billingState?.needsPlanPayment && (
                      <p className="text-[11px] text-amber-700 mt-3">
                        Trial ended. Subscribe at ${PLAN_PRICE_USD_PER_SEAT}/active user to restore full limits.
                        Current estimate: ${estimatedMonthly}/mo for {billableSeats}{' '}
                        {billableSeats === 1 ? 'user' : 'users'}.
                      </p>
                    )}
                  </div>

                  <OrgAiUsagePanel />

                  <div className="rounded-xl border border-indigo-100 bg-indigo-50/40 px-4 py-3 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-start gap-2 min-w-0">
                      <FiUsers className="shrink-0 mt-0.5 w-4 h-4 text-indigo-600" />
                      <div>
                        <p className="text-[12px] font-semibold text-slate-800">Code &amp; Ship access</p>
                        <p className="text-[11px] text-slate-600 mt-0.5">
                          Assign products per member in Org Settings — billing counts only assigned users.
                        </p>
                      </div>
                    </div>
                    <Link
                      to="/admin/org-settings?tab=product-access"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-indigo-200 bg-white text-xs font-semibold text-indigo-700 hover:bg-indigo-50 shrink-0"
                    >
                      Manage product access
                      <FiExternalLink className="w-3 h-3" />
                    </Link>
                  </div>
                </>
              )}

              {activeTab === 'products' && (
                <AidlcBillingMatrix
                  subscriptionProducts={subscription?.products}
                  billableSeatsByProduct={seatsByProduct}
                  hasPaidSubscription={subscription?.hasPaidSubscription}
                  billingConfigured={billingConfigured}
                  billingProvider={billingProvider}
                  onManageSubscription={() => void handleManageSubscription()}
                  portalLoading={portalLoading}
                />
              )}

              {activeTab === 'invoices' && (
                <div className="space-y-4">
                  <BillingTaxSetupPanel billingConfigured={billingConfigured} />
                  <BillingInvoicesPanel />
                </div>
              )}

              <p className="text-[11px] text-slate-400 text-center pb-2">
                Billing note: Plan is per org member; Code and Ship are per assigned user.{' '}
                <button type="button" onClick={handleContactSales} className="text-indigo-500 hover:underline">
                  Contact support
                </button>
              </p>
            </>
          )}
        </div>
      </div>

      {showContactModal && (
        <div className="fixed inset-0 z-overlay flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-flex items-center justify-center h-9 w-9 rounded-lg bg-emerald-50 border border-emerald-200 shrink-0">
                <FiMessageSquare className="text-emerald-600" size={18} />
              </span>
              <div>
                <h2 className="text-base font-bold text-slate-900">Contact sales</h2>
                <p className="text-xs text-slate-500">Enterprise and custom suite pricing</p>
              </div>
            </div>

            {contactSent ? (
              <div className="py-8 text-center">
                <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-3">
                  <FiCheck className="text-green-600" size={22} />
                </div>
                <p className="font-semibold text-slate-800">Message sent</p>
                <button
                  type="button"
                  onClick={() => setShowContactModal(false)}
                  className="mt-5 px-5 py-2 rounded-lg bg-slate-100 text-sm font-medium text-slate-700 hover:bg-slate-200"
                >
                  Close
                </button>
              </div>
            ) : (
              <>
                <p className="text-xs text-slate-500 mb-3">
                  We&apos;ll reply to your account email. Or write directly to{' '}
                  <a href="mailto:sales@cotaskr.com" className="text-indigo-600 hover:underline">
                    sales@cotaskr.com
                  </a>
                  .
                </p>
                <textarea
                  rows={5}
                  value={contactMessage}
                  onChange={(e) => setContactMessage(e.target.value)}
                  placeholder="Tell us about your team and which CoTaskr products you need…"
                  className="w-full rounded-lg border border-slate-200 text-sm px-3 py-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-emerald-400"
                  maxLength={2000}
                />
                {contactError && (
                  <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                    <FiAlertCircle className="shrink-0" />
                    {contactError}
                  </p>
                )}
                <div className="flex items-center gap-3 mt-4">
                  <button
                    type="button"
                    onClick={() => setShowContactModal(false)}
                    className="flex-1 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-600"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleSendContactMessage()}
                    disabled={contactLoading || !contactMessage.trim()}
                    className="flex-1 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <FiSend size={14} />
                    {contactLoading ? 'Sending…' : 'Send'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Billing;
